/**
 * Content Planner API Hooks
 *
 * React Query hooks for the keyword research and clustering feature.
 */

import { useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { contentPlannerService } from '@/services/contentPlannerService';
import { useTenantStore } from '@/stores/useTenantStore';
// Shared in-progress predicate — same helper drives the row affordance (T3),
// so the polling decision and the UI stay in lockstep on "what counts as generating".
import { isProcessing } from '@/components/content-planner/shared';
import type {
  TopicalMapSummary,
  TopicalMapDetail,
  TopicalMapListResponse,
  ClusterSummary,
  ClusterDetail,
  CreateTopicalMapRequest,
  UpdateClusterRequest,
  BulkUpdateClusterStatusRequest,
  ClusterFilters,
  TopicalMapStatistics,
  CommonWord,
  TopicSuggestionRequest,
  KeywordQualityInput,
  TitleSuggestionResponse,
  TopicSuggestionResponse,
  KeywordFeedAhrefsRefreshRequest,
  KeywordFeedConfig,
  KeywordFeedGenerateRequest,
  KeywordFeedListParams,
  KeywordFeedRefreshRequest,
  KeywordFeedUpdateRequest,
  KeywordVettingJobStatus,
  CalendarResponse,
} from '@/types/contentPlanner';

export interface CalendarQueryOptions {
  startDate?: string;
  endDate?: string;
  limit?: number;
  offset?: number;
  search?: string;
  status?: string;
  enabled?: boolean;
}

// =============================================================================
// Query Keys
// =============================================================================

export const contentPlannerKeys = {
  all: ['content-planner'] as const,
  titleSuggestions: (keyword: string, topic?: string) =>
    [...contentPlannerKeys.all, 'title-suggestions', keyword, topic ?? ''] as const,
  topicSuggestions: (seeds: string[], tenantId: string) =>
    [...contentPlannerKeys.all, 'topic-suggestions', tenantId, ...seeds.slice().sort()] as const,
  maps: (tenantId: string) => [...contentPlannerKeys.all, 'maps', tenantId] as const,
  mapsList: (tenantId: string, filters?: Record<string, unknown>) =>
    [...contentPlannerKeys.maps(tenantId), 'list', filters] as const,
  map: (tenantId: string, mapId: string) =>
    [...contentPlannerKeys.maps(tenantId), mapId] as const,
  mapStatistics: (tenantId: string, mapId: string) =>
    [...contentPlannerKeys.map(tenantId, mapId), 'statistics'] as const,
  clusters: (tenantId: string, mapId: string) =>
    [...contentPlannerKeys.map(tenantId, mapId), 'clusters'] as const,
  clustersList: (tenantId: string, mapId: string, filters?: ClusterFilters) =>
    [...contentPlannerKeys.clusters(tenantId, mapId), 'list', filters] as const,
  cluster: (tenantId: string, mapId: string, clusterId: string) =>
    [...contentPlannerKeys.clusters(tenantId, mapId), clusterId] as const,
  commonWords: (tenantId: string, mapId: string) =>
    [...contentPlannerKeys.map(tenantId, mapId), 'common-words'] as const,
  keywordFeed: (tenantId: string) =>
    [...contentPlannerKeys.all, 'keyword-feed', tenantId] as const,
  keywordFeedList: (tenantId: string, params?: KeywordFeedListParams) =>
    [...contentPlannerKeys.keywordFeed(tenantId), 'list', params] as const,
  keywordFeedConfig: (tenantId: string) =>
    [...contentPlannerKeys.keywordFeed(tenantId), 'config'] as const,
  keywordVettingJob: (tenantId: string, jobId: string) =>
    [...contentPlannerKeys.keywordFeed(tenantId), 'vetting-job', jobId] as const,
};

// =============================================================================
// Hooks - Topical Maps
// =============================================================================

export function useTopicalMaps(
  tenantId: string | null,
  params?: { status?: string; limit?: number; offset?: number }
) {
  return useQuery({
    queryKey: contentPlannerKeys.mapsList(tenantId || '', params),
    queryFn: () => contentPlannerService.listMaps(params),
    enabled: !!tenantId,
    refetchInterval: (query) => {
      // Poll while any map is processing
      // NOTE: Increased from 3s to 15s to reduce HTTP/1.1 connection pressure
      // 3s polling combined with SSE was exhausting the 6-connection-per-domain limit
      const data = query.state.data;
      if (data?.maps?.some((m: { status: string }) => !['completed', 'failed'].includes(m.status))) {
        return 15000; // Poll every 15 seconds (was 3s - too aggressive)
      }
      return false;
    },
    // Note: staleTime not added here because refetchInterval polling needs fresh data
  });
}

export function useTopicalMap(tenantId: string | null, mapId: string | null) {
  return useQuery({
    queryKey: contentPlannerKeys.map(tenantId || '', mapId || ''),
    queryFn: () => contentPlannerService.getMap(mapId!),
    enabled: !!tenantId && !!mapId,
    refetchInterval: (query) => {
      // Poll while processing
      // NOTE: Increased from 3s to 15s to reduce HTTP/1.1 connection pressure
      const data = query.state.data;
      if (data && !['completed', 'failed'].includes(data.status)) {
        return 15000; // Poll every 15 seconds (was 3s - too aggressive)
      }
      return false;
    },
    // Note: staleTime not added here because refetchInterval polling needs fresh data
  });
}

export function useTopicalMapStatistics(tenantId: string | null, mapId: string | null) {
  return useQuery({
    queryKey: contentPlannerKeys.mapStatistics(tenantId || '', mapId || ''),
    queryFn: () => contentPlannerService.getMapStatistics(mapId!),
    enabled: !!tenantId && !!mapId,
    staleTime: 60_000, // 60 seconds - statistics are stable once map is complete
    gcTime: 5 * 60 * 1000, // 5 minutes - keep in cache
  });
}

export function useCreateTopicalMap() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ tenantId, data }: { tenantId: string; data: CreateTopicalMapRequest }) =>
      contentPlannerService.createMap(data),
    onSuccess: (_, { tenantId }) => {
      queryClient.invalidateQueries({ queryKey: contentPlannerKeys.maps(tenantId) });
    },
  });
}

export function useDeleteTopicalMap() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ tenantId, mapId }: { tenantId: string; mapId: string }) =>
      contentPlannerService.deleteMap(mapId),
    onSuccess: (_, { tenantId }) => {
      queryClient.invalidateQueries({ queryKey: contentPlannerKeys.maps(tenantId) });
    },
  });
}

// =============================================================================
// Hooks - Clusters
// =============================================================================

export function useClusters(
  tenantId: string | null,
  mapId: string | null,
  filters?: ClusterFilters & { limit?: number; offset?: number }
) {
  return useQuery({
    queryKey: contentPlannerKeys.clustersList(tenantId || '', mapId || '', filters),
    queryFn: () => contentPlannerService.listClusters(mapId!, filters),
    enabled: !!tenantId && !!mapId,
    staleTime: 30_000, // 30 seconds - clusters change infrequently
    gcTime: 5 * 60 * 1000, // 5 minutes - keep in cache
  });
}

export function useCluster(tenantId: string | null, mapId: string | null, clusterId: string | null) {
  return useQuery({
    queryKey: contentPlannerKeys.cluster(tenantId || '', mapId || '', clusterId || ''),
    queryFn: () => contentPlannerService.getCluster(mapId!, clusterId!),
    enabled: !!tenantId && !!mapId && !!clusterId,
    staleTime: 30_000, // 30 seconds - cluster details change infrequently
    gcTime: 5 * 60 * 1000, // 5 minutes - keep in cache
  });
}

export function useUpdateCluster() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      tenantId,
      mapId,
      clusterId,
      data,
    }: {
      tenantId: string;
      mapId: string;
      clusterId: string;
      data: UpdateClusterRequest;
    }) => contentPlannerService.updateCluster(mapId, clusterId, data),
    onSuccess: (_, { tenantId, mapId }) => {
      queryClient.invalidateQueries({ queryKey: contentPlannerKeys.clusters(tenantId, mapId) });
    },
  });
}

export function useBulkUpdateClusterStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      tenantId,
      mapId,
      data,
    }: {
      tenantId: string;
      mapId: string;
      data: BulkUpdateClusterStatusRequest;
    }) => contentPlannerService.bulkUpdateClusterStatus(mapId, data),
    onSuccess: (_, { tenantId, mapId }) => {
      queryClient.invalidateQueries({ queryKey: contentPlannerKeys.clusters(tenantId, mapId) });
    },
  });
}

// =============================================================================
// Hooks - Keyword Feed
// =============================================================================

export function useKeywordFeed(
  tenantId: string | null,
  params?: KeywordFeedListParams,
) {
  return useQuery({
    queryKey: contentPlannerKeys.keywordFeedList(tenantId || '', params),
    queryFn: () => contentPlannerService.listKeywordFeed(params, tenantId ?? undefined),
    enabled: !!tenantId,
    staleTime: 30_000,
    gcTime: 5 * 60 * 1000,
  });
}

export function useRefreshKeywordFeed() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      tenantId,
      data,
    }: {
      tenantId: string;
      data?: KeywordFeedRefreshRequest;
    }) => contentPlannerService.refreshKeywordFeed(data, tenantId),
    onSuccess: (_, { tenantId }) => {
      queryClient.invalidateQueries({ queryKey: contentPlannerKeys.keywordFeed(tenantId) });
    },
  });
}

export function useRefreshKeywordFeedFromAhrefs() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      tenantId,
      data,
    }: {
      tenantId: string;
      data: KeywordFeedAhrefsRefreshRequest;
    }) => contentPlannerService.refreshKeywordFeedFromAhrefs(data, tenantId),
    onSuccess: (_, { tenantId }) => {
      queryClient.invalidateQueries({ queryKey: contentPlannerKeys.keywordFeed(tenantId) });
    },
  });
}

export function useUpdateKeywordFeedItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      tenantId,
      itemId,
      data,
    }: {
      tenantId: string;
      itemId: string;
      data: KeywordFeedUpdateRequest;
    }) => contentPlannerService.updateKeywordFeedItem(itemId, data, tenantId),
    onSuccess: (_, { tenantId }) => {
      queryClient.invalidateQueries({ queryKey: contentPlannerKeys.keywordFeed(tenantId) });
    },
  });
}

export function useGenerateKeywordFeedItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      tenantId,
      itemId,
      data,
    }: {
      tenantId: string;
      itemId: string;
      data?: KeywordFeedGenerateRequest;
    }) => contentPlannerService.generateKeywordFeedItem(itemId, data, tenantId),
    onSuccess: (_, { tenantId }) => {
      queryClient.invalidateQueries({ queryKey: contentPlannerKeys.keywordFeed(tenantId) });
      queryClient.invalidateQueries({ queryKey: [...contentPlannerKeys.all, 'calendar'] });
    },
  });
}

const ACTIVE_KEYWORD_VETTING_STATUSES = new Set<KeywordVettingJobStatus>([
  'queued',
  'running',
  'cancel_requested',
]);

export function useCreateKeywordVettingJob() {
  return useMutation({
    mutationFn: ({
      tenantId,
      feedItemIds,
    }: {
      tenantId: string;
      feedItemIds: string[];
    }) => contentPlannerService.createKeywordVettingJob(tenantId, feedItemIds),
  });
}

export function useKeywordVettingJob(tenantId: string | null, jobId: string | null) {
  const queryClient = useQueryClient();
  const invalidatedTerminalRef = useRef<string | null>(null);
  const query = useQuery({
    queryKey: contentPlannerKeys.keywordVettingJob(tenantId || '', jobId || ''),
    queryFn: () => contentPlannerService.getKeywordVettingJob(jobId!, tenantId!),
    enabled: !!tenantId && !!jobId,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1_000 * 2 ** attemptIndex, 5_000),
    refetchInterval: (currentQuery) => {
      const status = currentQuery.state.data?.status;
      return status && ACTIVE_KEYWORD_VETTING_STATUSES.has(status) ? 3_000 : false;
    },
  });

  useEffect(() => {
    if (!tenantId || !jobId || !query.data) return;
    if (ACTIVE_KEYWORD_VETTING_STATUSES.has(query.data.status)) return;
    const terminalKey = `${jobId}:${query.data.status}`;
    if (invalidatedTerminalRef.current === terminalKey) return;
    invalidatedTerminalRef.current = terminalKey;
    void queryClient.invalidateQueries({ queryKey: contentPlannerKeys.keywordFeed(tenantId) });
  }, [jobId, query.data, queryClient, tenantId]);

  return query;
}

export function useKeywordFeedConfig(tenantId: string | null) {
  return useQuery({
    queryKey: contentPlannerKeys.keywordFeedConfig(tenantId || ''),
    queryFn: () => contentPlannerService.getKeywordFeedConfig(tenantId ?? undefined),
    enabled: !!tenantId,
    staleTime: 60_000,
    gcTime: 5 * 60 * 1000,
  });
}

export function useUpdateKeywordFeedConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      tenantId,
      data,
    }: {
      tenantId: string;
      data: KeywordFeedConfig;
    }) => contentPlannerService.updateKeywordFeedConfig(data, tenantId),
    onSuccess: (_, { tenantId }) => {
      queryClient.invalidateQueries({ queryKey: contentPlannerKeys.keywordFeedConfig(tenantId) });
      queryClient.invalidateQueries({ queryKey: contentPlannerKeys.keywordFeed(tenantId) });
    },
  });
}

// =============================================================================
// Hooks - Utilities
// =============================================================================

export function useCommonWords(tenantId: string | null, mapId: string | null) {
  return useQuery({
    queryKey: contentPlannerKeys.commonWords(tenantId || '', mapId || ''),
    queryFn: () => contentPlannerService.getCommonWords(mapId!),
    enabled: !!tenantId && !!mapId,
    staleTime: 60_000, // 60 seconds - common words are stable once map is complete
    gcTime: 5 * 60 * 1000, // 5 minutes - keep in cache
  });
}

export function useExportMapCsv() {
  return useMutation({
    mutationFn: ({ tenantId, mapId }: { tenantId: string; mapId: string }) =>
      contentPlannerService.exportMapCsv(mapId),
    onSuccess: (blob, { mapId }) => {
      // Download the CSV
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `topical-map-${mapId}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    },
  });
}

// =============================================================================
// Hooks - Keyword Suggestions (LLM)
// =============================================================================

export function useKeywordSuggestions() {
  return useMutation({
    mutationFn: (data: {
      services_offered?: string[];
      customer_pain_points?: string;
      marketing_focus?: string;
      ideal_client?: string;
      business_name?: string;
      exclude_existing?: boolean;
    }) => contentPlannerService.getKeywordSuggestions(data),
  });
}

// =============================================================================
// Hooks - Keyword Dedup Check
// =============================================================================

/**
 * Debounced keyword duplicate check.
 * Only fires when keyword is >= 3 chars. Caches results for 30s.
 */
export function useKeywordDedupCheck(keyword: string | null, tenantId: string | null) {
  return useQuery({
    queryKey: ['keyword-dedup', tenantId, keyword],
    queryFn: () => contentPlannerService.checkKeywordDuplicate(keyword!),
    enabled: !!tenantId && !!keyword && keyword.trim().length >= 3,
    staleTime: 30_000,
    gcTime: 60_000,
    retry: 1,
  });
}

// =============================================================================
// Hooks - AI Topic Suggestions
// =============================================================================

export function useSuggestTopics() {
  return useMutation({
    mutationFn: (data: TopicSuggestionRequest) =>
      contentPlannerService.suggestTopics(data),
  });
}

export function useSeedKeywordAnalysis() {
  return useMutation({
    mutationFn: (data: {
      services_offered?: string[];
      customer_pain_points?: string;
      marketing_focus?: string;
      ideal_client?: string;
      business_name?: string;
    }) => contentPlannerService.analyzeSeedKeywords(data),
  });
}

// =============================================================================
// Hooks - Cached AI Suggestions (title + topic)
// =============================================================================

/**
 * Cached title suggestions — same keyword+topic reuses the result for 1 hour.
 * Pass `enabled: true` to trigger the fetch (replaces the old mutation pattern).
 */
export function useTitleSuggestions(
  keyword: string | null,
  topic?: string,
  enabled = false,
) {
  return useQuery<TitleSuggestionResponse>({
    queryKey: contentPlannerKeys.titleSuggestions(keyword ?? '', topic),
    queryFn: () => contentPlannerService.suggestTitles(keyword!, topic),
    enabled: enabled && !!keyword && keyword.trim().length > 0,
    staleTime: 60 * 60 * 1000,  // 1 hour — SERP results don't change quickly
    gcTime: 2 * 60 * 60 * 1000, // keep in cache 2 hours
    retry: 1,
  });
}

/**
 * Cached topic suggestions — same seed keywords reuse the result for 30 minutes.
 * Pass `enabled: true` to trigger the fetch.
 */
export function useCachedTopicSuggestions(
  data: TopicSuggestionRequest | null,
  tenantId: string | null,
  enabled = false,
) {
  const seeds = data?.seed_keywords ?? [];
  return useQuery<TopicSuggestionResponse>({
    queryKey: contentPlannerKeys.topicSuggestions(seeds, tenantId ?? ''),
    queryFn: () => contentPlannerService.suggestTopics(data!),
    enabled: enabled && !!data && seeds.length > 0 && !!tenantId,
    staleTime: 30 * 60 * 1000,  // 30 minutes
    gcTime: 60 * 60 * 1000,
    retry: 1,
  });
}

// =============================================================================
// Hooks - Keyword Quality Assessment
// =============================================================================

export function useKeywordQualityAssessment() {
  return useMutation({
    mutationFn: (keywords: KeywordQualityInput[]) =>
      contentPlannerService.assessKeywordQuality(keywords),
  });
}

// ── Calendar hooks ─────────────────────────────────────────────────────────

export function useCalendarItems(
  tenantId: string | null,
  options: CalendarQueryOptions = {},
) {
  return useQuery({
    queryKey: [...contentPlannerKeys.all, 'calendar', tenantId, options],
    queryFn: () => contentPlannerService.getCalendarItems({
      start_date: options.startDate,
      end_date: options.endDate,
      tenantId: tenantId || undefined,
      limit: options.limit,
      offset: options.offset,
      search: options.search,
      status: options.status,
    }),
    enabled: !!tenantId && (options.enabled ?? true),
    staleTime: 60_000, // 60s — calendar data doesn't change that fast
  });
}

/**
 * Workspace colors used for multi-workspace mode (cycle if more than 6 workspaces).
 * Consumed as an inline `backgroundColor`, so the first two entries can follow the
 * brand tokens; the remaining four are chart-style distinguishing colors with no
 * token equivalent.
 */
export const WS_PALETTE = [
  'var(--color-teal)',
  'var(--color-coral)',
  '#6366F1',
  '#F59E0B',
  '#10B981',
  '#8B5CF6',
]

/**
 * Fetches one globally paginated calendar/list result set across workspaces.
 * Each item is tagged with `_wsColor` for chip color differentiation.
 */
export function useMultiWorkspaceCalendar(
  tenantIds: string[],
  optionsOrStartDate: CalendarQueryOptions | string = {},
  endDate?: string,
) {
  const options: CalendarQueryOptions =
    typeof optionsOrStartDate === 'string'
      ? { startDate: optionsOrStartDate, endDate }
      : optionsOrStartDate;

  const colorByTenant = new Map(
    tenantIds.map((tenantId, i) => [tenantId, WS_PALETTE[i % WS_PALETTE.length]]),
  );

  const query = useQuery({
    queryKey: [...contentPlannerKeys.all, 'calendar', 'multi', tenantIds, options],
    queryFn: () => contentPlannerService.getCalendarItems({
      start_date: options.startDate,
      end_date: options.endDate,
      tenantIds,
      limit: options.limit,
      offset: options.offset,
      search: options.search,
      status: options.status,
    }),
    enabled: tenantIds.length > 0 && (options.enabled ?? true),
    // Pagination: the offset is part of the queryKey, so turning the page starts
    // a brand-new query with no cached data. Without this, `total` would fall
    // back to 0 mid-fetch, collapsing totalPages to 1 and bouncing the user back
    // to page 1. Keeping the previous page's data holds `total` steady (and
    // avoids the list flashing empty between pages).
    placeholderData: (previousData) => previousData,
    staleTime: 60_000, // 60s stale — refetchInterval below drives the live updates
    // Always refetch on mount so a freshly-created row shows up the instant the
    // wizard navigates here (belt-and-suspenders alongside the create-side cache
    // invalidation; mount-refetch covers the case where the cache is still warm).
    refetchOnMount: 'always',
    refetchInterval: (query) => {
      // If the last fetch failed, back off to let the backend recover.
      // (Matches the house pattern in useContentList / useTopicalMaps.)
      if (query.state.error) return 30_000;
      // NOTE: refetchInterval sees the RAW queryFn data (CalendarResponse), not
      // the `select`-mapped shape — so read items off query.state.data.items.
      const items = query.state.data?.items;
      if (!items || items.length === 0) return false;
      // Poll every 15s while ANY item is mid-generation; stop polling otherwise.
      // Uses the shared `isProcessing` predicate (T1/T3 contract).
      return items.some((item) => isProcessing(item)) ? 15_000 : false;
    },
    select: (data: CalendarResponse) => {
      const mappedItems = data.items.map((item) => {
        const tenantId = item.tenant_id ?? tenantIds[0];
        return {
          ...item,
          _wsColor: tenantId ? colorByTenant.get(tenantId) : undefined,
          _wsTenant: tenantId,
        };
      });
      const offset = data.offset ?? options.offset ?? 0;
      const limit = data.limit ?? options.limit;
      const items =
        typeof limit === 'number' && mappedItems.length > limit
          ? mappedItems.slice(offset, offset + limit)
          : mappedItems;
      return {
        ...data,
        items,
        total: data.total ?? mappedItems.length,
        limit,
        offset,
      };
    },
  });

  const stats = query.data?.stats ?? {
    scheduled: 0,
    draft: 0,
    generating: 0,
    published: 0,
    failed: 0,
    total: 0,
    this_month: 0,
  };

  return {
    isLoading: query.isLoading,
    // `isFetching` covers background polls too. `isPlaceholderData` is true
    // ONLY while a NEW query key (i.e. a page/filter change) is in flight and
    // we're still showing the previous page — that is precisely the
    // "switching pages" moment the list shows a loading state for. Note
    // `isLoading` is FALSE during page turns because placeholderData supplies
    // data, so callers must use this to detect a page transition.
    isFetching: query.isFetching,
    isPlaceholderData: query.isPlaceholderData,
    items: query.data?.items ?? [],
    stats,
    total: query.data?.total ?? 0,
    limit: query.data?.limit ?? options.limit,
    offset: query.data?.offset ?? options.offset ?? 0,
  };
}

export function useScheduleContent() {
  const queryClient = useQueryClient();
  const { currentTenant } = useTenantStore();

  return useMutation({
    mutationFn: ({ contentId, scheduledDate }: { contentId: string; scheduledDate: string | null }) =>
      contentPlannerService.scheduleContent(contentId, scheduledDate),
    onSuccess: () => {
      // Invalidate calendar + content list
      queryClient.invalidateQueries({ queryKey: [...contentPlannerKeys.all, 'calendar', currentTenant] });
      queryClient.invalidateQueries({ queryKey: ['content', 'list', currentTenant] });
    },
  });
}
